import { useMemo } from 'react';

import type { TalentDiscoveryRespUserV3 } from '@revelio/data-access';

export interface WorkHistoryCompany {
  rcid: number;
  companyName: string;
  title: string;
  startDate: string;
  endDate: string;
  salary: number;
  isCurrent: boolean;
}

const MAX_COMPANIES = 8;

/**
 * Extracts and deduplicates companies from a user's work history.
 * Filters out entries with RCID <= 0 and deduplicates by RCID
 * (keeping the most recent stint if the same company appears twice).
 * The first work_history entry is marked as the current employer.
 */
export const useWorkHistoryCompanies = (
  user: TalentDiscoveryRespUserV3 | null
): { companies: WorkHistoryCompany[] } => {
  const companies = useMemo((): WorkHistoryCompany[] => {
    if (!user?.work_history || user.work_history.length === 0) return [];

    const seen = new Set<number>();
    const result: WorkHistoryCompany[] = [];

    for (
      let i = 0;
      i < user.work_history.length && result.length < MAX_COMPANIES;
      i++
    ) {
      const entry = user.work_history[i];
      if (!entry || entry.RCID <= 0) continue;
      if (seen.has(entry.RCID)) continue;

      seen.add(entry.RCID);
      result.push({
        rcid: entry.RCID,
        companyName: entry.current_company || `Company #${entry.RCID}`,
        title: entry.current_title || '',
        startDate: entry.start_date || '',
        endDate: entry.end_date || '',
        salary: entry.salary_total > 0 ? entry.salary_total : entry.salary_base,
        isCurrent: i === 0,
      });
    }

    return result;
  }, [user]);

  return { companies };
};
